import { encounterState, setEncounterArena } from '../../content/bosses';
import { COLOSSUS_ARENA_RADIUS } from '../../content/zones/colossus';
import { angleTo } from '../../core/math';
import { rollLoot } from '../../game/loot';
import type { ZoneOpts } from '../../game/netHooks';
import type { Actor } from '../../sim/actor';
import type { SimEvent } from '../../sim/events';
import { registry } from '../../sim/registry';
import type { World } from '../../sim/world';
import { consumeRested } from '../bounties/dayCycle';
import { type DeathView, type HudView, type InstanceEnv, type ResultView, clockText, emptyHud, once } from './env';
import { claimColossus, colossusClaimed } from './lockouts';
import { grantDrop } from './rewards';
import { COLOSSUS } from './rules';
import { InstanceScript, type Msg, type ScriptState } from './script';

/**
 * COLOSSUS HUNT (D7) — the Guardian Raid at launch (research 01 §4, 02 §5.6):
 * one colossus at the arena centre, a 20-minute limit (timeout = the hunt
 * fails), and 3 revives shared by the whole party (revive in place); when
 * they are spent and everyone is down the hunt fails. Boss HP already scales
 * with the party (`world.partySize`). Rewards are a daily claim (rested ×2):
 * Colossus Marks, Leap Crystals and ore, accessories and a chance of a Rune
 * Stone, plus first-clear gold.
 */

export const COLOSSUS_ZONES = ['colossus_cinderhide', 'colossus_gloomfang', 'colossus_rimebound', 'colossus_dunestrider'] as const;
export const colossusOf = (zone: string): string => zone.replace(/^colossus_/, '');

export type ColossusPhase = 'fight' | 'won' | 'failed';

export interface ColossusState extends ScriptState {
  boss: string;
  bossId: number;
  /** Seconds left on the 20-minute limit. */
  time: number;
  elapsed: number;
  /** Revives left, shared by the party. */
  revives: number;
  phase: ColossusPhase;
  reason: '' | 'time' | 'wipe';
}

/** Removes a boss and its mechanic helpers without a death (failed hunts, gate resets). */
export function despawnEncounter(world: World, boss: Actor): void {
  if (boss.monster) {
    const e = encounterState(boss);
    for (const id of [...e.helpers, ...e.adds]) if (world.actors.has(id)) world.removeActor(id);
    for (const ar of world.areas) {
      if (ar.ownerId === boss.id && !ar.dead) {
        ar.dead = true;
        world.emit({ type: 'areaEnd', id: ar.id });
      }
    }
  }
  world.removeActor(boss.id);
}

export class ColossusScript extends InstanceScript<ColossusState> {
  /** A revive request is in flight (cleared by the host's answer, or after 3 s if the host changed). */
  private asked = false;
  private askedAt = 0;

  constructor(env: InstanceEnv, opts: ZoneOpts, zone: string) {
    super(env, opts, { seq: 0, boss: colossusOf(zone), bossId: -1, time: COLOSSUS.timeLimit, elapsed: 0, revives: COLOSSUS.revives, phase: 'fight', reason: '' });
  }

  get bossName(): string {
    return registry.monsters.get(this.state.boss)?.name ?? 'the Colossus';
  }

  protected override hostInit(): void {
    this.spawnBoss();
  }

  private spawnBoss(): void {
    const sp = this.spawnPoint('boss');
    const start = this.spawnPoint('start');
    const boss = this.z.spawnMonster(this.state.boss, COLOSSUS.level, { x: sp.x, z: sp.z }, { facing: angleTo(sp, start) });
    setEncounterArena(boss, { x: sp.x, z: sp.z }, COLOSSUS_ARENA_RADIUS);
    this.update({ bossId: boss.id });
  }

  protected override becameHost(): void {
    const s = this.state;
    if (s.phase !== 'fight') return;
    const boss = this.world.actors.get(s.bossId);
    // Encounter state lives on the host's brain record: re-anchor the arena on the adopted boss.
    if (boss) setEncounterArena(boss, this.spawnPoint('boss'), COLOSSUS_ARENA_RADIUS);
    // The expected host never arrived: this peer spawns the colossus itself.
    else this.spawnBoss();
  }

  protected override hostTick(dt: number): void {
    const s = this.state;
    if (s.phase !== 'fight') return;
    s.time -= dt;
    s.elapsed += dt;
    if (s.time <= 0) {
      s.time = 0;
      this.fail('time');
      return;
    }
    const all = this.players();
    if (all.length > 0 && all.every((p) => !p.alive) && s.revives <= 0) this.fail('wipe');
  }

  protected override mirrorTick(dt: number): void {
    const s = this.state;
    if (s.phase !== 'fight') return;
    s.time = Math.max(0, s.time - dt);
    s.elapsed += dt;
  }

  protected override hostEvent(ev: SimEvent): void {
    if (ev.type === 'death' && ev.id === this.state.bossId && this.state.phase === 'fight') this.update({ phase: 'won' });
  }

  private fail(reason: 'time' | 'wipe'): void {
    const boss = this.world.actors.get(this.state.bossId);
    if (boss) despawnEncounter(this.world, boss);
    this.update({ phase: 'failed', reason });
  }

  /** Host: a fallen player asks for one of the shared revives. */
  protected override request(from: string, data: Msg): void {
    if (data.t !== 'rev') return;
    const s = this.state;
    const dead = this.players().find((p) => p.owner === from && !p.alive);
    if (s.phase === 'fight' && s.revives > 0 && dead) {
      this.update({ revives: s.revives - 1 });
      this.toPeer(from, 'revOk');
    } else this.toPeer(from, 'revNo');
  }

  protected override notice(data: Msg): void {
    if (data.t === 'revOk') {
      this.asked = false;
      this.reviveMe(COLOSSUS.reviveHp);
    } else if (data.t === 'revNo') {
      this.asked = false;
      this.env.toast('No revives left', '#e5484d');
    }
  }

  protected override changed(prev: ColossusState, next: ColossusState): void {
    if (next.revives < prev.revives && next.phase === 'fight') this.env.toast(`A shared revive was used — ${next.revives} left`, '#ffd070');
    if (next.phase === prev.phase) return;
    if (next.phase === 'won') {
      this.env.announce(`${this.bossName} has been slain!`, 'success');
      if (this.me && !this.me.alive) this.reviveMe(1);
      this.reward();
    } else if (next.phase === 'failed') {
      this.env.announce(next.reason === 'time' ? 'Time is up — the hunt has failed' : 'Your party has fallen — the hunt has failed', 'warning');
    }
  }

  private reward(): void {
    const p = this.env.progression;
    if (!p || !once(this.env, `${this.run}:reward`)) return;
    this.env.syncPeriods();
    const id = this.state.boss;
    p.char.counters[`colossus_${id}`] = (p.char.counters[`colossus_${id}`] ?? 0) + 1;
    if (!claimColossus(p.char, p.account)) {
      this.env.toast("Today's Colossus claim is already used — practice hunt, no chest", '#e5a24d');
      this.env.save();
      return;
    }
    const rested = consumeRested(p.char, 'colossus');
    const drop = rollLoot('colossus', { level: COLOSSUS.level, rng: p.rng, rested });
    const firstKey = `colossus_first_${id}`;
    if (!p.char.flags[firstKey]) {
      p.char.flags[firstKey] = true;
      drop.gold += COLOSSUS.firstClearGold[id] ?? 100;
    }
    const sub = `${rested ? 'Rested ×2 · ' : ''}Slain in ${clockText(this.state.elapsed)}`;
    this.env.rewards(grantDrop(p, drop, this.bossName, `${this.bossName} — Hunt Complete`, sub));
    this.env.save();
  }

  view(): HudView {
    const s = this.state;
    const v = emptyHud('colossus', 'Colossus Hunt', this.bossName);
    if (s.phase === 'fight') v.timer = { label: 'Time limit', seconds: s.time, warn: s.time < 120 };
    v.revives = { n: s.revives, max: COLOSSUS.revives, label: 'Shared revives' };
    const p = this.env.progression;
    if (p && colossusClaimed(p.char, p.account) && s.phase !== 'won') v.badges.push({ text: 'Claimed today', tone: 'grey' });
    else if (p && p.char.daily.colossusRested >= 20 && s.phase === 'fight') v.badges.push({ text: 'Rested ×2', tone: 'gold' });
    if (s.phase === 'won') v.badges.push({ text: 'Slain', tone: 'green' });
    if (s.phase === 'failed') v.badges.push({ text: 'Failed', tone: 'red' });
    v.actions.push(s.phase === 'fight' ? this.leaveAction('Abandon the hunt?') : { id: 'leave', label: 'Leave', tone: 'gold' });
    return v;
  }

  override deathView(): DeathView {
    const s = this.state;
    if (this.asked && this.clock - this.askedAt > 3) this.asked = false;
    const can = s.phase === 'fight' && s.revives > 0;
    return {
      title: 'You have fallen',
      text: can ? `Your party shares ${s.revives} revive${s.revives === 1 ? '' : 's'}. Rise where you fell.` : s.phase === 'fight' ? 'No revives remain. If everyone falls, the hunt fails.' : 'The hunt is over.',
      countdown: null,
      actions: [...(can ? [{ id: 'revive', label: this.asked ? 'Reviving…' : `Revive (${s.revives} left)`, tone: 'gold' as const, disabled: this.asked }] : []), this.leaveAction()],
    };
  }

  override resultView(): ResultView | null {
    const s = this.state;
    if (s.phase !== 'failed') return null;
    const p = this.env.progression;
    const claimed = !!p && colossusClaimed(p.char, p.account);
    return {
      title: 'Hunt Failed',
      subtitle: this.bossName,
      tone: 'loss',
      lines: [s.reason === 'time' ? 'The 20-minute limit ran out.' : 'Every hunter fell and no revives remained.', claimed ? "Today's claim was already used." : "Today's claim is still available — try again."],
      actions: [{ id: 'leave', label: 'Return', tone: 'gold' }],
    };
  }

  override action(id: string): void {
    if (id === 'leave') this.env.leave();
    else if (id === 'revive' && !this.asked && !this.me?.alive) {
      this.asked = true;
      this.askedAt = this.clock;
      this.toHost('rev');
    }
  }
}
